import { useCallback, useEffect, useRef, useState } from 'react';
import { toErrorMessage } from '@reflex/plugin-api/errors';
import { getTranscriptionCapability, transcribeAudio } from '../api';

/**
 * Voice input for browsers with no usable Web Speech API — in practice
 * Firefox, which ships no `SpeechRecognition` implementation at all.
 *
 * Chrome and Safari stream audio to a vendor recognition service and hand
 * back a growing transcript. This hook reproduces that shape from parts the
 * browser does have: `getUserMedia` for the stream, an `AnalyserNode` to
 * spot the pauses between utterances, and `MediaRecorder` to cut each
 * utterance into its own Opus segment. Every segment is posted to the chat
 * plugin's transcription route and the recognized text is emitted through
 * {@link UseAudioTranscriptionOptions.onFinalTranscript} — the same callback
 * the native path uses, so the composer behaves identically either way.
 *
 * The one visible difference is interim text: no local recognizer means
 * there is nothing to show until a segment comes back, so the interim
 * channel reports transcription progress instead of a partial guess.
 */

/**
 * Normalized RMS above which a polling tick counts as speech. Chosen well
 * above the noise floor of a laptop mic with echo cancellation on, and low
 * enough that a quiet speaker still registers.
 */
const SPEECH_RMS_THRESHOLD = 0.02;

/** Quiet stretch that ends an utterance and sends the segment. */
const SILENCE_HOLD_MS = 900;

/**
 * Hard cut for a segment even if the speaker never pauses. Bounds both the
 * upload size and how long the user waits to see any text at all.
 */
const MAX_SEGMENT_MS = 20_000;

/**
 * Speech a segment must contain before it is worth uploading. Filters out
 * door slams and keyboard noise that trip the level check for one tick.
 */
const MIN_SPEECH_MS = 300;

/** Level-meter poll interval; also the resolution of the speech accounting. */
const POLL_MS = 100;

/**
 * Opus at 24 kbps is transparent for speech and keeps a 20s segment around
 * 60 KB, so the upload stays far below the route's body limit.
 */
const AUDIO_BITS_PER_SECOND = 24_000;

/**
 * Recorder container preference. Ogg/Opus leads because every server-side
 * provider accepts it (Google's audio input rejects WebM); Firefox produces
 * it natively, and the WebM entries only matter for a Chromium browser that
 * somehow lost its Web Speech API.
 */
const PREFERRED_MIME_TYPES = [
  'audio/ogg;codecs=opus',
  'audio/ogg',
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
];

/** Interim text shown while a recorded segment is being transcribed. */
const TRANSCRIBING_LABEL = 'Transcribing…';

interface AudioContextCtor {
  new (): AudioContext;
}

/**
 * The first recorder container this browser supports, or `null` when
 * recording is unavailable (no `MediaRecorder`, no `getUserMedia`, or an
 * insecure context where `mediaDevices` is undefined).
 */
export function pickRecorderMimeType(): string | null {
  if (typeof window === 'undefined') return null;
  if (typeof MediaRecorder === 'undefined') return null;
  if (!navigator?.mediaDevices?.getUserMedia) return null;
  for (const mimeType of PREFERRED_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported?.(mimeType)) return mimeType;
  }
  return null;
}

/**
 * Cached capability probe. Whether the org has a provider key that can
 * transcribe does not change mid-session, and every composer on the page
 * mounts this hook, so the answer is fetched once and shared. A failed probe
 * resolves to `false` rather than rejecting — a mic button that cannot work
 * should stay hidden, not throw.
 */
let capabilityProbe: Promise<boolean> | null = null;

function probeTranscriptionEnabled(): Promise<boolean> {
  capabilityProbe ??= getTranscriptionCapability()
    .then((capability) => capability.enabled)
    .catch(() => false);
  return capabilityProbe;
}

/** Test seam: forget the cached probe so the next mount re-fetches. */
export function resetTranscriptionCapabilityProbe(): void {
  capabilityProbe = null;
}

async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  // Chunked so a long segment does not blow the argument limit of `apply`.
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

export interface UseAudioTranscriptionOptions {
  /**
   * Whether this fallback should be live at all. The composer passes
   * `false` when the browser has a native `SpeechRecognition`, which keeps
   * Chrome and Safari off the network path (and off the capability probe).
   */
  enabled: boolean;
  /** Fires with each transcribed segment, in the order it was spoken. */
  onFinalTranscript?: (text: string) => void;
  /** Fires with progress text while segments are in flight; '' when idle. */
  onInterimTranscript?: (text: string) => void;
  /** Fires with a display-ready message when voice input fails. */
  onError?: (error: string) => void;
}

export interface UseAudioTranscriptionResult {
  supported: boolean;
  listening: boolean;
  error: string | null;
  start: () => void;
  stop: () => void;
  toggle: () => void;
}

/** One in-progress recording segment's level accounting. */
interface SegmentState {
  startedAt: number;
  lastLoudAt: number;
  speechMs: number;
}

export function useAudioTranscription(
  options: UseAudioTranscriptionOptions,
): UseAudioTranscriptionResult {
  const { enabled, onFinalTranscript, onInterimTranscript, onError } = options;

  const [supported, setSupported] = useState(false);
  const [recording, setRecording] = useState(false);
  const [pending, setPending] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const onFinalRef = useRef(onFinalTranscript);
  const onInterimRef = useRef(onInterimTranscript);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onFinalRef.current = onFinalTranscript;
    onInterimRef.current = onInterimTranscript;
    onErrorRef.current = onError;
  }, [onFinalTranscript, onInterimTranscript, onError]);

  const mimeTypeRef = useRef<string | null>(null);
  const recordingRef = useRef(false);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const segmentRef = useRef<SegmentState | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  // Explicitly backed by an `ArrayBuffer` (not `ArrayBufferLike`): the Web
  // Audio typings reject a possibly-shared buffer.
  const levelBufferRef = useRef<Uint8Array<ArrayBuffer> | null>(null);
  const pendingRef = useRef(0);
  // Serializes uploads so transcripts land in the order they were spoken
  // even when a short segment finishes before a longer earlier one.
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      setSupported(false);
      return;
    }
    const mimeType = pickRecorderMimeType();
    mimeTypeRef.current = mimeType;
    if (!mimeType) {
      setSupported(false);
      return;
    }
    let cancelled = false;
    void probeTranscriptionEnabled().then((ok) => {
      if (!cancelled) setSupported(ok);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const releaseCapture = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    analyserRef.current = null;
    levelBufferRef.current = null;
    const ctx = audioContextRef.current;
    audioContextRef.current = null;
    if (ctx) void ctx.close().catch(() => {});
    const stream = streamRef.current;
    streamRef.current = null;
    for (const track of stream?.getTracks() ?? []) track.stop();
    segmentRef.current = null;
    chunksRef.current = [];
    recorderRef.current = null;
  }, []);

  const reportInterim = useCallback(() => {
    onInterimRef.current?.(pendingRef.current > 0 ? TRANSCRIBING_LABEL : '');
  }, []);

  const enqueueSegment = useCallback(
    (blob: Blob) => {
      pendingRef.current += 1;
      if (mountedRef.current) setPending(pendingRef.current);
      reportInterim();
      queueRef.current = queueRef.current
        .then(async () => {
          const audio = await blobToBase64(blob);
          const result = await transcribeAudio({
            audio,
            mimeType: blob.type || mimeTypeRef.current || 'audio/ogg',
          });
          const text = result.text.trim();
          if (text) onFinalRef.current?.(text);
        })
        .catch((err: unknown) => {
          const message = toErrorMessage(err);
          if (mountedRef.current) setError(message);
          onErrorRef.current?.(message);
        })
        .finally(() => {
          pendingRef.current = Math.max(0, pendingRef.current - 1);
          if (mountedRef.current) setPending(pendingRef.current);
          reportInterim();
        });
    },
    [reportInterim],
  );

  /**
   * Start recording the next utterance on the already-open stream. Called
   * once when the mic opens and again after every cut, so recording is
   * continuous from the speaker's point of view.
   */
  const startSegment = useCallback(() => {
    const stream = streamRef.current;
    const mimeType = mimeTypeRef.current;
    if (!stream || !mimeType) return;
    const recorder = new MediaRecorder(stream, {
      mimeType,
      audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
    });
    chunksRef.current = [];
    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onstop = () => {
      const chunks = chunksRef.current;
      const segment = segmentRef.current;
      chunksRef.current = [];
      segmentRef.current = null;
      recorderRef.current = null;
      if (chunks.length > 0 && segment && segment.speechMs >= MIN_SPEECH_MS) {
        enqueueSegment(new Blob(chunks, { type: mimeType }));
      }
      // A cut mid-session immediately opens the next segment; a cut caused
      // by `stop()` is the last one, so the capture devices are released
      // here rather than before the recorder had flushed its final data.
      if (recordingRef.current) startSegment();
      else releaseCapture();
    };
    const now = Date.now();
    segmentRef.current = { startedAt: now, lastLoudAt: now, speechMs: 0 };
    recorderRef.current = recorder;
    recorder.start();
  }, [enqueueSegment, releaseCapture]);

  const cutSegment = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
  }, []);

  /**
   * Current input level as normalized RMS. Returns 1 when no analyser could
   * be created (an environment without `AudioContext`), which degrades the
   * session to fixed {@link MAX_SEGMENT_MS} cuts instead of silence-aligned
   * ones — still usable, just less natural.
   */
  const readLevel = useCallback((): number => {
    const analyser = analyserRef.current;
    const buffer = levelBufferRef.current;
    if (!analyser || !buffer) return 1;
    analyser.getByteTimeDomainData(buffer);
    let sumSquares = 0;
    for (let i = 0; i < buffer.length; i++) {
      const sample = (buffer[i]! - 128) / 128;
      sumSquares += sample * sample;
    }
    return Math.sqrt(sumSquares / buffer.length);
  }, []);

  const tick = useCallback(() => {
    const segment = segmentRef.current;
    if (!segment) return;
    const now = Date.now();
    if (readLevel() > SPEECH_RMS_THRESHOLD) {
      segment.speechMs += POLL_MS;
      segment.lastLoudAt = now;
    }
    const spokeEnough = segment.speechMs >= MIN_SPEECH_MS;
    if (now - segment.startedAt >= MAX_SEGMENT_MS) {
      cutSegment();
      return;
    }
    if (spokeEnough && now - segment.lastLoudAt >= SILENCE_HOLD_MS) cutSegment();
  }, [cutSegment, readLevel]);

  const attachLevelMeter = useCallback((stream: MediaStream) => {
    const Ctor: AudioContextCtor | undefined =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext;
    if (!Ctor) return;
    try {
      const ctx = new Ctor();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      ctx.createMediaStreamSource(stream).connect(analyser);
      audioContextRef.current = ctx;
      analyserRef.current = analyser;
      levelBufferRef.current = new Uint8Array(new ArrayBuffer(analyser.fftSize));
    } catch {
      // Level metering is an optimization, not a requirement — a browser
      // that refuses the audio graph still gets time-sliced segments.
    }
  }, []);

  const stop = useCallback(() => {
    if (!recordingRef.current) return;
    recordingRef.current = false;
    setRecording(false);
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      // `onstop` flushes the final segment and then releases capture.
      recorder.stop();
    } else {
      releaseCapture();
    }
  }, [releaseCapture]);

  const start = useCallback(() => {
    if (recordingRef.current) return;
    const mimeType = mimeTypeRef.current ?? pickRecorderMimeType();
    if (!mimeType) return;
    mimeTypeRef.current = mimeType;
    setError(null);
    recordingRef.current = true;
    setRecording(true);

    void (async () => {
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
      } catch (err: unknown) {
        recordingRef.current = false;
        if (mountedRef.current) setRecording(false);
        const denied =
          err instanceof Error && (err.name === 'NotAllowedError' || err.name === 'SecurityError');
        const message = denied
          ? 'Microphone access was blocked. Allow it in your browser to use voice input.'
          : toErrorMessage(err);
        if (mountedRef.current) setError(message);
        onErrorRef.current?.(message);
        return;
      }
      // The user (or an unmount) may have cancelled while the permission
      // prompt was open — drop the stream instead of recording unasked.
      if (!recordingRef.current || !mountedRef.current) {
        for (const track of stream.getTracks()) track.stop();
        return;
      }
      streamRef.current = stream;
      attachLevelMeter(stream);
      startSegment();
      pollRef.current = setInterval(tick, POLL_MS);
    })();
  }, [attachLevelMeter, startSegment, tick]);

  /**
   * While the last segment is still uploading the button reads as "stop",
   * so a click must not open a new recording — it is a no-op until the
   * queue drains and the control returns to its idle state.
   */
  const toggle = useCallback(() => {
    if (recordingRef.current) stop();
    else if (pendingRef.current === 0) start();
  }, [start, stop]);

  useEffect(() => {
    return () => {
      recordingRef.current = false;
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.onstop = null;
        recorder.ondataavailable = null;
        try {
          recorder.stop();
        } catch {
          // Already stopping; the capture release below is what matters.
        }
      }
      releaseCapture();
    };
  }, [releaseCapture]);

  return {
    supported,
    listening: recording || pending > 0,
    error,
    start,
    stop,
    toggle,
  };
}
